import useCandidate from "@/composables/use-candidate";
import useAwards from "@/composables/use-awards";
import useProducts from "@/composables/use-products";
import {
  default as useTicketsStore,
  validTicketHolder,
} from "@/stores/tickets";
import useSWRV from "swrv";
import { type Component, computed } from "vue";
import { TicketIcon, StarIcon } from "@heroicons/vue/24/outline";
import { format, sub, parseISO } from "date-fns";

export type Alert = {
  name: string;
  description: string;
  route: string;
  icon: Component;
  highlight?: string;
};

export type Options = {};

export default function useAlerts() {
  const ticketsStore = useTicketsStore();

  const { awardYear, afterCutoff, awardsCeremonyOver } = useAwards();
  const {
    status: candidateStatus,
    candidate,
    ceremonyNight,
    claimedNomination,
  } = useCandidate();

  const { vipUpgradeAvailable } = useProducts();

  useSWRV("/tickets", ticketsStore.getAllTickets, {
    refreshInterval: 5 * 60_000,
    shouldRetryOnError: false,
  });

  const shouldAssignTickets = computed(() => {
    return (
      ceremonyNight &&
      candidate.value?.tickets.length &&
      !awardsCeremonyOver.value &&
      candidate.value?.tickets.some((t) => !validTicketHolder(t))
    );
  });

  const canUpgradeVip = computed(() => {
    return (
      !awardsCeremonyOver.value &&
      ticketsStore.upgradeableTicketCount &&
      vipUpgradeAvailable
    );
  });

  const canClaimNomination = computed(() => {
    return (
      candidateStatus.value.isNominee &&
      !claimedNomination.value &&
      !afterCutoff.value
    );
  });

  const alerts = computed(() => {
    const items: Array<Alert> = [];
    if (shouldAssignTickets.value) {
      let description = `Please assign your ${awardYear.value} Awards Ceremony tickets to your guests who are attending.`;
      if (ceremonyNight.value?.date) {
        description += ` They must be assigned by ${format(
          sub(parseISO(ceremonyNight.value.date), { days: 3 }),
          "MMM. do"
        )}`;
      }
      items.push({
        name: "Assign Tickets",
        description,
        route: "awards-ceremony",
        icon: TicketIcon,
        highlight: "text-red-600",
      });
    }
    if (canUpgradeVip.value) {
      items.push({
        name: "Upgrade to VIP!",
        description: `Click here to upgrade your ${awardYear.value} Awards Ceremony tickets!`,
        route: "upgrade-to-vip",
        icon: TicketIcon,
        highlight: "text-green-600",
      });
    }
    if (canClaimNomination.value) {
      items.push({
        name: "Claim Nomination!",
        description: "Click here to claim your nomination!",
        route: "claim-nomination",
        icon: StarIcon,
        highlight: "text-primary",
      });
    }
    return items;
  });

  return {
    alerts,
    canClaimNomination,
    canUpgradeVip,
    shouldAssignTickets,
  };
}
